import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../utils/api';
import { Save } from 'lucide-react';
import ImageUploadField from './ImageUploadField';
import FocalPointPicker from './FocalPointPicker';
import SectionsEditor from './SectionsEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PhotoPageContentPanel = () => {
  const [content, setContent] = useState({
    hero_image_url: '',
    hero_heading: 'Crystal Media',
    hero_subheading: 'Timeless photography for your most cherished moments',
    hero_focal_desktop: { x: 50, y: 50 },
    hero_focal_mobile: { x: 50, y: 50 },
    cta_bg_image_url: '',
    cta_overlay_color: '#000000',
    cta_overlay_opacity: 0.4,
    cta_focal_desktop: { x: 50, y: 50 },
    cta_focal_mobile: { x: 50, y: 50 },
    sections_enabled: true,
    sections: [],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/content/photo-page-content`);
      const data = await res.json();
      if (data) setContent(prev => ({ ...prev, ...data }));
    } catch (e) {
      console.error('Failed to load photo page content:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const save = async () => {
    setSaving(true);
    setMessage('');
    try {
      await api.updatePhotoPageContent(content);
      setMessage('Photo page content saved!');
      setTimeout(() => setMessage(''), 3000);
    } catch (e) {
      setMessage('Error: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="py-12 text-center text-gray-500">Loading...</div>;

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold">Photo Page Content</h2>
        <p className="text-sm text-gray-500 mt-1">Manage the hero section and CTA background on the Photo page</p>
      </div>

      {message && (
        <div className={`p-3 rounded text-sm ${message.includes('Error') ? 'bg-red-50 text-red-800' : 'bg-green-50 text-green-800'}`}>{message}</div>
      )}

      {/* Hero Section */}
      <div className="border rounded-lg p-6 space-y-5">
        <h3 className="text-lg font-semibold border-b pb-3">Hero Section</h3>
        <p className="text-sm text-gray-500">This hero appears at the top of the Photo page, above the collections grid.</p>

        <ImageUploadField
          value={content.hero_image_url}
          onChange={(url) => setContent({ ...content, hero_image_url: url })}
          folder="photo-hero"
          label="Hero Background Image"
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Heading</label>
            <input
              type="text"
              data-testid="photo-hero-heading"
              value={content.hero_heading}
              onChange={(e) => setContent({ ...content, hero_heading: e.target.value })}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-black"
              placeholder="Crystal Media"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Subheading</label>
            <input
              type="text"
              data-testid="photo-hero-subheading"
              value={content.hero_subheading}
              onChange={(e) => setContent({ ...content, hero_subheading: e.target.value })}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-black"
              placeholder="Timeless photography..."
            />
          </div>
        </div>

        {/* Focal Points for Hero */}
        {content.hero_image_url && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
            <FocalPointPicker
              imageUrl={content.hero_image_url}
              focalPoint={content.hero_focal_desktop || { x: 50, y: 50 }}
              onChange={(fp) => setContent({ ...content, hero_focal_desktop: fp })}
              label="Desktop Focus Point"
            />
            <FocalPointPicker
              imageUrl={content.hero_image_url}
              focalPoint={content.hero_focal_mobile || { x: 50, y: 50 }}
              onChange={(fp) => setContent({ ...content, hero_focal_mobile: fp })}
              label="Mobile Focus Point"
            />
          </div>
        )}
      </div>

      {/* CTA Background */}
      <div className="border rounded-lg p-6 space-y-5">
        <h3 className="text-lg font-semibold border-b pb-3">CTA Section Background</h3>
        <p className="text-sm text-gray-500">Background image for the "Ready to capture your story?" section.</p>

        <ImageUploadField
          value={content.cta_bg_image_url}
          onChange={(url) => setContent({ ...content, cta_bg_image_url: url })}
          folder="photo-cta"
          label="CTA Background Image"
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Overlay Color</label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                data-testid="photo-cta-overlay-color"
                value={content.cta_overlay_color}
                onChange={(e) => setContent({ ...content, cta_overlay_color: e.target.value })}
                className="w-10 h-10 border rounded cursor-pointer"
              />
              <input
                type="text"
                value={content.cta_overlay_color}
                onChange={(e) => setContent({ ...content, cta_overlay_color: e.target.value })}
                className="flex-1 px-3 py-2 border rounded text-sm"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Overlay Opacity ({Math.round(content.cta_overlay_opacity * 100)}%)</label>
            <input
              type="range"
              data-testid="photo-cta-overlay-opacity"
              min="0" max="1" step="0.05"
              value={content.cta_overlay_opacity}
              onChange={(e) => setContent({ ...content, cta_overlay_opacity: parseFloat(e.target.value) })}
              className="w-full mt-2"
            />
          </div>
        </div>

        {/* Focal Points for CTA */}
        {content.cta_bg_image_url && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
            <FocalPointPicker
              imageUrl={content.cta_bg_image_url}
              focalPoint={content.cta_focal_desktop || { x: 50, y: 50 }}
              onChange={(fp) => setContent({ ...content, cta_focal_desktop: fp })}
              label="Desktop Focus Point"
            />
            <FocalPointPicker
              imageUrl={content.cta_bg_image_url}
              focalPoint={content.cta_focal_mobile || { x: 50, y: 50 }}
              onChange={(fp) => setContent({ ...content, cta_focal_mobile: fp })}
              label="Mobile Focus Point"
            />
          </div>
        )}

        {/* Preview */}
        {content.cta_bg_image_url && (
          <div className="relative rounded overflow-hidden" style={{ height: '120px' }}>
            <img src={content.cta_bg_image_url} alt="CTA Preview" className="w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ backgroundColor: content.cta_overlay_color, opacity: content.cta_overlay_opacity }} />
            <span className="absolute inset-0 flex items-center justify-center text-white text-sm font-medium">Preview</span>
          </div>
        )}
      </div>

      {/* Content Sections */}
      <div className="border rounded-lg p-6 space-y-4">
        <div className="flex items-center justify-between border-b pb-3">
          <div>
            <h3 className="text-lg font-semibold">Content Sections</h3>
            <p className="text-xs text-gray-400 mt-1">Toggle sections on/off for the public Photo page</p>
          </div>
          <label className="flex items-center gap-2 cursor-pointer" data-testid="photo-sections-toggle">
            <span className="text-sm text-gray-500">{content.sections_enabled !== false ? 'Visible' : 'Hidden'}</span>
            <div className="relative">
              <input
                type="checkbox"
                checked={content.sections_enabled !== false}
                onChange={(e) => setContent({ ...content, sections_enabled: e.target.checked })}
                className="sr-only"
              />
              <div className={`w-10 h-5 rounded-full transition-colors ${content.sections_enabled !== false ? 'bg-black' : 'bg-gray-300'}`}>
                <div className={`w-4 h-4 rounded-full bg-white shadow transform transition-transform mt-0.5 ${content.sections_enabled !== false ? 'translate-x-5.5 ml-[22px]' : 'translate-x-0.5 ml-[2px]'}`} />
              </div>
            </div>
          </label>
        </div>
        <SectionsEditor
          sections={content.sections || []}
          onChange={(sections) => setContent({ ...content, sections })}
          folder="photo-sections"
        />
      </div>

      {/* Save */}
      <div className="flex justify-end">
        <button onClick={save} disabled={saving} className="px-6 py-2 bg-black text-white text-sm rounded hover:bg-gray-800 disabled:opacity-50 flex items-center gap-2" data-testid="save-photo-page-content">
          <Save size={14} />{saving ? 'Saving...' : 'Save Photo Page Content'}
        </button>
      </div>
    </div>
  );
};

export default PhotoPageContentPanel;
